import React, { useEffect, useState } from 'react';
import { AutoComplete, Image, Space } from 'antd';
import { useAppDispatch } from '../../../hooks/redux';
import { searchTagsCachedAction } from '../../../redux/actions/tagActions';
import { Tag as TagType } from '../../../redux/actions/tagActions/types';
import { alertService } from '../../../services';
import { Tag } from '../../../styles/common.styles';
import { SearchInput } from '../../favoriteProductTagModal/FavoriteProductTagsModal.styles';
import { splitAndSortCommonTags } from '../../../utils/tagUtils';
import { PickerContainer, SearchContainer, TagsDivider } from './TagsPicker.styles';

interface Props {
    selectedTags: TagType[];
    setSelectedTags: (tags: TagType[]) => void;
}

const TagsPicker: React.FC<Props> = ({ selectedTags, setSelectedTags }) => {
    const dispatch = useAppDispatch();

    const [search, setSearch] = useState('');
    const [searchResults, setSearchResults] = useState<TagType[]>([]);

    useEffect(() => {
        dispatch(searchTagsCachedAction({ curPage: 1, pageSize: 20, searchString: search }))
            .then((res: any) => {
                const items = res.payload?.data?.items ?? res.payload?.data ?? [];
                setSearchResults(splitAndSortCommonTags(items));
            })
            .catch(() => alertService.error('Tag search failed'))
    }, []);

    const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const handleSearch = (val: string) => {
        setSearch(val);

        globalThis.clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = globalThis.setTimeout(() => {
            dispatch(searchTagsCachedAction({ curPage: 1, pageSize: 20, searchString: val }))
                .then((res: any) => {
                    const items = res.payload?.data?.items ?? res.payload?.data ?? [];
                    setSearchResults(splitAndSortCommonTags(items));
                })
                .catch(() => alertService.error('Tag search failed'))
        }, 250);
    };

    const addTagToSelection = (tag: TagType) => {
        if (!selectedTags.some((t) => t.id === tag.id)) {
            setSelectedTags([...selectedTags, tag]);
        }
    };

    const removeSelectedTag = (tagId: number) => {
        setSelectedTags(selectedTags.filter((t) => t.id !== tagId));
    };

    const filteredSearchResults = searchResults.filter((r) => !selectedTags.some((st) => st.id === r.id));

    const basicTags = filteredSearchResults.filter(t => !t.organizationId);
    const orgTags = filteredSearchResults.filter(t => t.organizationId).sort((a, b) => a.name.localeCompare(b.name));

    const options: any[] = [];
    if (basicTags.length > 0) {
        options.push(...basicTags.map((item) => ({
            value: item.id?.toString() ?? item.name,
            label: (
                <Tag color={item.color?.display} borderColor={item.color?.baseColor}>
                    {item.name}
                </Tag>
            ),
        })));
    }
    if (orgTags.length > 0) {
        if (basicTags.length > 0) {
            options.push({
                value: 'divider',
                className: 'tag-picker-divider',
                label: <TagsDivider />,
                disabled: true,
            });
        }
        options.push(...orgTags.map((item) => ({
            value: item.id?.toString() ?? item.name,
            label: (
                <Tag color={item.color?.display} borderColor={item.color?.baseColor}>
                    {item.name}
                </Tag>
            ),
        })));
    }

    return (
        <PickerContainer>
            <SearchContainer>
                <AutoComplete
                    style={{ width: 320 }}
                    popupClassName="tag-picker-dropdown"
                    getPopupContainer={(trigger) => trigger.parentElement || document.body}
                    options={options}
                    onSelect={(value) => {
                        if (value === 'divider') {
                            return;
                        }
                        const id = Number.parseInt(value, 10);
                        const item = searchResults.find((s) => s.id === id);
                        if (item) {
                            addTagToSelection(item);
                        }
                    }}
                    onSearch={(val) => handleSearch(val)}
                    value={search}
                >
                    <SearchInput
                        placeholder="Search tag names"
                        prefix={
                            <Image
                                src="src/assets/images/magnifying-glass.svg"
                                preview={false}
                            />
                        }
                        onChange={(e) => handleSearch(e.target.value)}
                        value={search}
                    />
                </AutoComplete>
            </SearchContainer>

            {selectedTags?.length > 0 && (
                <div>
                    <Space wrap style={{ gap: 8 }}>
                        {splitAndSortCommonTags(selectedTags).map((t) => (
                            <Tag
                                key={t.id}
                                closable
                                onClose={() => removeSelectedTag(t.id)}
                                color={t.color?.display}
                                borderColor={t.color?.baseColor}
                            >
                                {t.name}
                            </Tag>
                        ))}
                    </Space>
                </div>
            )}
        </PickerContainer>
    );
};

export default TagsPicker;
